import axios from "axios";
import { API } from "@/lib/api";

// Session lives in an httpOnly cookie set by the backend; JS never sees the token.
// CSRF: double-submit — readable cookie value echoed in X-CSRF-Token on state-changing calls.
const CSRF_COOKIE = "mp_admin_csrf";

const readCookie = (name) =>
  document.cookie.split("; ").find((c) => c.startsWith(`${name}=`))?.split("=").slice(1).join("=") || "";

export const adminApi = axios.create({ baseURL: `${API}/admin`, withCredentials: true });

adminApi.interceptors.request.use((cfg) => {
  if (!["get", "head", "options"].includes((cfg.method || "get").toLowerCase())) {
    cfg.headers["X-CSRF-Token"] = readCookie(CSRF_COOKIE);
  }
  return cfg;
});

adminApi.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 && !err.config?.url?.includes("/auth/")) {
      if (!window.location.pathname.startsWith("/admin/login")) window.location.assign("/admin/login");
    }
    return Promise.reject(err);
  }
);

const data = (p) => p.then((r) => r.data);

export const adminLogin = (username, password) => data(adminApi.post("/auth/login", { username, password }));
export const adminLogout = () => data(adminApi.post("/auth/logout"));
export const adminMe = () => data(adminApi.get("/auth/me"));
export const adminDashboard = () => data(adminApi.get("/dashboard"));
export const adminSettings = () => data(adminApi.get("/settings"));
export const updateAdminSettings = (body) => data(adminApi.put("/settings", body));
export const adminPicks = (params) => data(adminApi.get("/picks", { params }));
export const adminPick = (id) => data(adminApi.get(`/picks/${id}`));
export const createPick = (body) => data(adminApi.post("/picks", body));
export const updatePick = (id, body) => data(adminApi.put(`/picks/${id}`, body));
export const publishPick = (id) => data(adminApi.post(`/picks/${id}/publish`));
export const unpublishPick = (id) => data(adminApi.post(`/picks/${id}/unpublish`));
export const deletePick = (id) => data(adminApi.delete(`/picks/${id}`));
export const settlePick = (id, status, legs, grading_evidence) => data(adminApi.post(`/picks/${id}/settle`, { status, legs, grading_evidence }));
export const gradeSuggestion = (id) => data(adminApi.get(`/picks/${id}/grade-suggestion`));
export const adminSettlementStatus = () => data(adminApi.get("/settlement-status"));
export const adminRunSettlement = () => data(adminApi.post("/settle-picks"));
export const adminHistorical = (season) => data(adminApi.get("/historical", { params: { season } }));
export const previewHistorical = (body) => data(adminApi.post("/historical/preview", body));
export const createHistorical = (body) => data(adminApi.post("/historical", body));
export const updateHistorical = (id, body) => data(adminApi.put(`/historical/${id}`, body));
export const deleteHistorical = (id) => data(adminApi.delete(`/historical/${id}`));
export const uploadEvidence = (file) => { const fd = new FormData(); fd.append("file", file); return data(adminApi.post("/historical/evidence", fd, { headers: { "Content-Type": "multipart/form-data" } })); };
export const adminFixtures = (params) => data(adminApi.get("/fixtures", { params }));
export const adminSearchPlayers = (q, team_ids) => data(adminApi.get("/players", { params: { q, team_ids } }));
export const adminPropStats = (params) => data(adminApi.get("/prop-stats", { params }));
export const correctPick = (id, status, reason) => data(adminApi.post(`/picks/${id}/correct`, { status, reason }));

export function apiError(err) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  const status = err?.response?.status;
  const { method, url, baseURL } = err?.config || {};
  const full = url && baseURL && !/^https?:\/\//.test(url) ? `${baseURL.replace(/\/$/, "")}/${url.replace(/^\//, "")}` : url;
  const where = method && full ? ` (${method.toUpperCase()} ${full.replace(/^https?:\/\/[^/]+/, "")})` : "";
  if (!err?.response) return `Could not reach the Money Printer server${where}. Check your connection and try again — nothing was lost.`;
  if (status === 404) return `The server did not recognise this request${where}. It is usually restarting after an update — wait a few seconds and try again; your form is intact.`;
  if (status === 401) return "Your admin session has expired. Sign in again — your form is intact.";
  if (status === 403) return "Security check failed (CSRF). Reload the page and try again.";
  if (status === 502 || status === 503 || status === 504) return `The server is restarting${where}. Try again in a few seconds — your form is intact.`;
  return `${err?.message || "Something went wrong"}${where}`;
}
